/**
 * Product Order Interface Component
 * 
 * Main interface for product ordering with stock selection
 */

import { useState, useEffect } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import VariationSelector from './VariationSelector';
import StockCellsGrid from './StockCellsGrid';
import NoStockOrderInterface from './NoStockOrderInterface';
import RawMaterialOrderInterface from './RawMaterialOrderInterface';
import StandbyOrderInterface from './StandbyOrderInterface';
import AddToCartIntegration from './AddToCartIntegration';

const ProductOrderInterface = ({ 
    productId, 
    productInfo, 
    warehouseInfo, 
    isGuest, 
    isMobile, 
    localization,
    onError,
    onGuestCheckout 
}) => {
    const [loading, setLoading] = useState(false);
    const [variations, setVariations] = useState([]);
    const [selectedVariation, setSelectedVariation] = useState(null);
    const [stockCells, setStockCells] = useState([]);
    const [selectedPicks, setSelectedPicks] = useState([]);
    const [totalQuantity, setTotalQuantity] = useState(0);
    const [orderStep, setOrderStep] = useState('variation'); // variation -> stock/raw-material/standby/no-stock -> summary

    const standbySelected = (() => {
        if (selectedVariation && typeof selectedVariation.is_standby !== 'undefined') {
            return Boolean(selectedVariation.is_standby);
        }
        if (selectedVariation && selectedVariation.product_type) {
            return selectedVariation.product_type === 'standby';
        }
        if (warehouseInfo && warehouseInfo.product_type) {
            return warehouseInfo.product_type === 'standby';
        }
        return false;
    })();
    
    /**
     * Load product variations on component mount
     */
    useEffect(() => {
        loadProductVariations();
    }, [productId]);
    
    /**
     * Update total quantity when picks change (only for stock-based products)
     */
    useEffect(() => {
        // Only update totalQuantity from selectedPicks if we're in stock mode
        // For no-stock items, totalQuantity is managed separately
        if (orderStep === 'stock' && warehouseInfo.has_stock_cells) {
            const total = selectedPicks.reduce((sum, pick) => sum + (pick.qty || 0), 0);
            setTotalQuantity(total);
        }
    }, [selectedPicks, orderStep, warehouseInfo.has_stock_cells]);
    
    /**
     * Load product variations
     */
    const loadProductVariations = async () => {
        setLoading(true);
        
        try {
            const response = await fetch(
                `${localization.apiUrl}product/${productId}/variations`,
                {
                    headers: {
                        'X-WP-Nonce': localization.nonce
                    }
                }
            );
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const variationData = await response.json();
            
            setVariations(variationData);
            
            // For raw material products, skip variation selection and go directly to raw-material interface
            if (warehouseInfo.is_raw_material) {
                setOrderStep('raw-material');
            }
            // Auto-select if only one variation (simple product)
            else if (variationData.length === 1) {
                handleVariationSelect(variationData[0]);
            }
            
        } catch (err) {
            onError(err, 'loadProductVariations');
        } finally {
            setLoading(false);
        }
    };
    
    /**
     * Handle variation selection
     */
    const handleVariationSelect = async (variation) => {
        setSelectedVariation(variation);
        setSelectedPicks([]);
        setStockCells([]);
        setTotalQuantity(0);

        const variationIsStandby = Boolean(
            (variation && typeof variation.is_standby !== 'undefined' && variation.is_standby) ||
            (variation && variation.product_type === 'standby') ||
            (warehouseInfo && warehouseInfo.product_type === 'standby')
        );
        
        // Determine order flow based on product type
        if (warehouseInfo.is_raw_material) {
            // Raw material product - use specialized interface
            setOrderStep('raw-material');
        } else if (variationIsStandby) {
            // Standby products skip stock selection and only need quantity
            setOrderStep('standby');
        } else if (warehouseInfo.has_stock_cells) {
            // Regular product with stock cells
            await loadStockCells(variation.variation_id);
            setOrderStep('stock');
        } else {
            // No stock cells - go to no-stock interface
            setOrderStep('no-stock');
        }
    };
    
    /**
     * Load stock cells for selected variation
     */
    const loadStockCells = async (variationId) => {
        setLoading(true);
        
        try {
            const response = await fetch(
                `${localization.apiUrl}product/${productId}/variation/${variationId}/stock-cells`,
                {
                    headers: {
                        'X-WP-Nonce': localization.nonce
                    }
                }
            );
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const stockData = await response.json();
            
            // If no stock cells returned, switch to no-stock mode
            if (!stockData || stockData.length === 0) {
                setOrderStep('no-stock');
                setStockCells([]);
            } else {
                setStockCells(stockData);
            }
            
        } catch (err) {
            // If stock loading fails, switch to no-stock mode for graceful fallback
            setOrderStep('no-stock');
            setStockCells([]);
        } finally {
            setLoading(false);
        }
    };
    
    /**
     * Handle stock selection changes
     */
    const handleStockSelection = (stockId, quantity) => {
        setSelectedPicks(prev => {
            const existingIndex = prev.findIndex(pick => pick.stock_id === stockId);
            
            if (quantity === 0) {
                // Remove pick if quantity is 0
                return prev.filter(pick => pick.stock_id !== stockId);
            } else if (existingIndex >= 0) {
                // Update existing pick
                const updated = [...prev];
                updated[existingIndex] = { stock_id: stockId, qty: quantity };
                return updated;
            } else {
                // Add new pick
                return [...prev, { stock_id: stockId, qty: quantity }];
            }
        });
    };
    
    /**
     * Handle no-stock quantity change
     */
    const handleNoStockQuantityChange = (quantity) => {
        setTotalQuantity(quantity);
        // For no-stock items, we don't need to clear selectedPicks
        // since they're not used and clearing them would trigger useEffect
    };
    
    /**
     * Validate current selection
     */
    const validateSelection = async () => {
        if (warehouseInfo.has_stock_cells && !standbySelected && selectedPicks.length === 0) {
            onError(__('請選擇至少一個庫存位置', 'frontend-order-system'), 'validation');
            return false;
        }
        
        if (totalQuantity === 0) {
            onError(__('請設定數量', 'frontend-order-system'), 'validation');
            return false;
        }
        
        // For products with stock cells, validate with API
        if (!standbySelected && warehouseInfo.has_stock_cells && selectedPicks.length > 0) {
            try {
                const response = await fetch(
                    `${localization.apiUrl}validate-stock-picks`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-WP-Nonce': localization.nonce
                        },
                        body: JSON.stringify({
                            product_id: productId,
                            variation_id: selectedVariation.variation_id,
                            stock_picks: selectedPicks
                        })
                    }
                );
                
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }
                
                const validation = await response.json();
                
                if (!validation.success) {
                    onError(validation.errors.join(', '), 'validation');
                    return false;
                }
                
            } catch (err) {
                onError(err, 'validateSelection');
                return false;
            }
        }
        
        return true;
    };
    
    /**
     * Handle add to cart
     */
    const handleAddToCart = async (result) => {
        if (result && result.success) {
            if (warehouseInfo.is_no_existed || standbySelected) {
                const productUrl = localization?.currentProduct?.permalink || window.location.href;
                window.location.href = productUrl;
                return;
            }

            setOrderStep('summary');
            return;
        }

        if (!(await validateSelection())) {
            return;
        }
        
        setOrderStep('summary');
    };
    
    /**
     * Handle guest checkout initiation
     */
    const handleBuyNow = async () => {
        if (!(await validateSelection())) {
            return;
        }
        
        if (isGuest) {
            // Show guest checkout modal
            onGuestCheckout({
                productId,
                variationId: selectedVariation?.variation_id || 0,
                quantity: totalQuantity,
                stockPicks: selectedPicks
            });
        } else {
            // For logged-in users, add to cart and redirect to checkout
            await handleAddToCart();
            // Redirect will be handled by the parent component
        }
    };
    
    /**
     * Render loading state
     */
    if (loading) {
        return (
            <div className="fos-loading">
                <div className="spinner"></div>
                <span>{__('載入中...', 'frontend-order-system')}</span>
            </div>
        );
    }
    
    /**
     * Render variation selection step
     */
    if (orderStep === 'variation' && variations.length > 1) {
        return (
            <VariationSelector
                variations={variations}
                selectedVariation={selectedVariation}
                onVariationSelect={handleVariationSelect}
                isMobile={isMobile}
            />
        );
    }
    
    /**
     * Render stock selection step
     */
    if (orderStep === 'stock' && warehouseInfo.has_stock_cells) {
        return (
            <div>
                {/* Show selected variation */}
                {selectedVariation && variations.length > 1 && (
                    <div className="fos-selected-variation" style={{ 
                        padding: '12px 20px', 
                        background: '#f8f9fa', 
                        borderBottom: '1px solid #e1e1e1',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <span><strong>{__('已選擇:', 'frontend-order-system')}</strong> {selectedVariation.variation_name}</span>
                        <button 
                            className="fos-btn-secondary"
                            style={{ fontSize: '0.8rem', padding: '4px 8px' }}
                            onClick={() => {
                                setOrderStep('variation');
                                setSelectedVariation(null);
                                setSelectedPicks([]);
                                setStockCells([]);
                            }}
                        >
                            {__('更改', 'frontend-order-system')}
                        </button>
                    </div>
                )}
                
                <StockCellsGrid
                    stockCells={stockCells}
                    selectedPicks={selectedPicks}
                    onStockSelection={handleStockSelection}
                    isMobile={isMobile}
                />
                
                {/* Summary and Actions */}
                {totalQuantity > 0 && (
                    <div className="fos-summary-section">
                        <div className="fos-summary-content">
                            <div className="fos-summary-row">
                                <span className="fos-summary-label">{__('總數量:', 'frontend-order-system')}</span>
                                <span className="fos-summary-value">{totalQuantity}</span>
                            </div>
                            
                            <div className="fos-selected-locations">
                                <h4>{__('選擇的位置:', 'frontend-order-system')}</h4>
                                <div className="fos-location-tags">
                                    {selectedPicks.map((pick) => {
                                        const stock = stockCells.find(s => s.stock_id === pick.stock_id);
                                        return stock ? (
                                            <span key={pick.stock_id} className="fos-location-tag">
                                                {stock.display_name}({pick.qty})
                                            </span>
                                        ) : null;
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                
                <AddToCartIntegration
                    productId={productId}
                    variationId={selectedVariation?.variation_id || 0}
                    quantity={totalQuantity}
                    stockPicks={selectedPicks}
                    isGuest={isGuest}
                    localization={localization}
                    onAddToCart={handleAddToCart}
                    onBuyNow={handleBuyNow}
                    disabled={totalQuantity === 0}
                />
            </div>
        );
    }

    /**
     * Render summary step (after add to cart success)
     */
    if (orderStep === 'summary') {
        return (
            <div className="fos-summary">
                <div style={{
                    padding: '20px',
                    background: '#d4edda',
                    color: '#155724',
                    border: '1px solid #c3e6cb',
                    borderRadius: '4px',
                    textAlign: 'center',
                    marginBottom: '20px'
                }}>
                    <h3 style={{ margin: '0 0 10px 0' }}>✓ {__('成功加入購物車！', 'frontend-order-system')}</h3>
                    <p style={{ margin: 0 }}>
                        {selectedVariation?.variation_name} × {totalQuantity}
                    </p>
                </div>

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                        className="fos-btn fos-btn-secondary"
                        style={{ flex: 1 }}
                        onClick={() => {
                            // Reset to allow another purchase
                            const nextStep = warehouseInfo.is_raw_material
                                ? 'raw-material'
                                : standbySelected
                                    ? 'standby'
                                    : (warehouseInfo.has_stock_cells ? 'stock' : 'no-stock');
                            setOrderStep(nextStep);
                            setSelectedPicks([]);
                            setTotalQuantity(0);
                        }}
                    >
                        {__('繼續購買', 'frontend-order-system')}
                    </button>
                    <button
                        className="fos-btn fos-btn-primary"
                        style={{ flex: 1 }}
                        onClick={() => {
                            window.location.href = localization.cartUrl || '/cart/';
                        }}
                    >
                        {__('查看購物車', 'frontend-order-system')}
                    </button>
                </div>
            </div>
        );
    }

    /**
     * Render raw material interface
     */
    if (orderStep === 'raw-material' && warehouseInfo.is_raw_material) {
        return (
            <div>
                {/* Show selected variation */}
                {selectedVariation && variations.length > 1 && (
                    <div className="fos-selected-variation" style={{ 
                        padding: '12px 20px', 
                        background: '#fff3e0', 
                        borderBottom: '1px solid #ffcc02',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <span><strong>{__('已選擇:', 'frontend-order-system')}</strong> {selectedVariation.variation_name}</span>
                        <button 
                            className="fos-btn-secondary"
                            style={{ fontSize: '0.8rem', padding: '4px 8px' }}
                            onClick={() => {
                                setOrderStep('variation');
                                setSelectedVariation(null);
                            }}
                        >
                            {__('更改', 'frontend-order-system')}
                        </button>
                    </div>
                )}
                
                <RawMaterialOrderInterface
                    productId={productId}
                    productInfo={productInfo}
                    selectedVariation={selectedVariation}
                    isGuest={isGuest}
                    isMobile={isMobile}
                    localization={localization}
                    onError={onError}
                    onVariationSelect={handleVariationSelect}
                />
            </div>
        );
    }
    
    /**
     * Render standby interface
     */
    if (orderStep === 'standby') {
        return (
            <div>
                {/* Show selected variation */}
                {selectedVariation && variations.length > 1 && (
                    <div className="fos-selected-variation" style={{ 
                        padding: '12px 20px', 
                        background: '#e8f5e9', 
                        borderBottom: '1px solid #a5d6a7',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <span><strong>{__('已選擇:', 'frontend-order-system')}</strong> {selectedVariation.variation_name}</span>
                        <button 
                            className="fos-btn-secondary"
                            style={{ fontSize: '0.8rem', padding: '4px 8px' }}
                            onClick={() => {
                                setOrderStep('variation');
                                setSelectedVariation(null);
                                setTotalQuantity(0);
                            }}
                        >
                            {__('更改', 'frontend-order-system')}
                        </button>
                    </div>
                )}

                <StandbyOrderInterface
                    quantity={totalQuantity}
                    onQuantityChange={handleNoStockQuantityChange}
                    isMobile={isMobile}
                />

                <AddToCartIntegration
                    productId={productId}
                    variationId={selectedVariation?.variation_id || 0}
                    quantity={totalQuantity}
                    stockPicks={[]} // Standby products do not require stock picks
                    isGuest={isGuest}
                    localization={localization}
                    onAddToCart={handleAddToCart}
                    onBuyNow={handleBuyNow}
                    disabled={totalQuantity === 0}
                />
            </div>
        );
    }

    /**
     * Render no-stock interface
     */
    if (orderStep === 'no-stock' && !warehouseInfo.has_stock_cells) {
        return (
            <div>
                {/* Show selected variation */}
                {selectedVariation && variations.length > 1 && (
                    <div className="fos-selected-variation" style={{ 
                        padding: '12px 20px', 
                        background: '#f8f9fa', 
                        borderBottom: '1px solid #e1e1e1',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <span><strong>{__('已選擇:', 'frontend-order-system')}</strong> {selectedVariation.variation_name}</span>
                        <button 
                            className="fos-btn-secondary"
                            style={{ fontSize: '0.8rem', padding: '4px 8px' }}
                            onClick={() => {
                                setOrderStep('variation');
                                setSelectedVariation(null);
                                setTotalQuantity(0);
                            }}
                        >
                            {__('更改', 'frontend-order-system')}
                        </button>
                    </div>
                )}
                
                <NoStockOrderInterface
                    productId={productId}
                    selectedVariation={selectedVariation}
                    quantity={totalQuantity}
                    onQuantityChange={handleNoStockQuantityChange}
                    isMobile={isMobile}
                />
                
                <AddToCartIntegration
                    productId={productId}
                    variationId={selectedVariation?.variation_id || 0}
                    quantity={totalQuantity}
                    stockPicks={[]} // No stock picks for pre-order items
                    isGuest={isGuest}
                    localization={localization}
                    onAddToCart={handleAddToCart}
                    onBuyNow={handleBuyNow}
                    disabled={totalQuantity === 0}
                    isPreorder={true}
                />
            </div>
        );
    }
    
    /**
     * Default: Fallback for unexpected states
     */
    return (
        <div className="fos-error">
            <span className="fos-error-icon">⚠️</span>
            {__('載入中發生錯誤，請重試', 'frontend-order-system')}
        </div>
    );
};

export default ProductOrderInterface;
